import React, { useState, useMemo, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

import { useReservationsData } from './hooks/useReservationsData';
import { useTimeline } from './hooks/useTimeline';

import PlanningHeader from './components/PlanningHeader';
import IntervenantFilter from './components/IntervenantFilter';
import TimelineGrid from './components/TimelineGrid';
import DayPlanningDialog from './components/DayPlanningDialog';
import ReservationDetailsDialog from './components/ReservationDetailsDialog';

const PlanningPro = () => {
  const { user } = useAuth();

  // 🩺 Distinction des rôles : Intervenant (3) vs Praticien (4)
  const isIntervenant = user?.role === 3;
  const isPraticien = user?.role === 4;

  // États UI
  const [vue, setVue] = useState(7);
  const [selectedIntervenant, setSelectedIntervenant] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // { offre, jour }
  const [selected, setSelected] = useState(null); // { id, offre }

  // 🔄 Référence pour savoir si on a modifié un compte-rendu dans la modale
  const hasModifiedRef = useRef(false);

  // Données + actions API
  const {
    apiUrl,
    token,
    intervenants,
    offres,
    reservations,
    loading,
    updateReservationStatus,
    fetchReservations,
  } = useReservationsData({ user, selectedIntervenant });

  // Timeline + scroll
  const {
    scrollContainerRef,
    currentMonth,
    aujourdhuiStr,
    joursTimeline,
    indexToday,
    largeurCellule,
    handleScroll,
    scrollByDays,
    scrollToToday,
  } = useTimeline({ vue, loading });

  // Filtres
  const filteredReservations = useMemo(() => {
    if (!selectedIntervenant) return reservations;
    return reservations.filter(
      (r) =>
        String(r.originalData?.user?.id_user) === String(selectedIntervenant)
    );
  }, [reservations, selectedIntervenant]);

  const filteredOffres = useMemo(() => {
    if (!selectedIntervenant) return offres;
    return offres.filter(
      (o) => String(o.intervenantId) === String(selectedIntervenant)
    );
  }, [offres, selectedIntervenant]);

  // ⚡ Enrichissement de la réservation sélectionnée pour y injecter/garantir les infos du client dans la commande
  const selectedReservation = useMemo(() => {
    if (!selected) return null;
    const res = reservations.find((r) => r.id === selected.id);
    if (!res) return null;

    // Récupération sécurisée du client (depuis la structure standard ou originalData)
    const clientInfo =
      res.client ||
      res.originalData?.client ||
      res.originalData?.user?.client ||
      null;

    // Si la commande existe, on lui injecte les infos du client pour qu'elle y ait accès direct
    let enrichedCommande = res.commande;
    if (enrichedCommande && clientInfo) {
      // Si la commande est un tableau ou un objet unique, on s'assure d'y propager le client
      if (Array.isArray(enrichedCommande)) {
        enrichedCommande = enrichedCommande.map(cmd => ({ ...cmd, client: clientInfo }));
      } else if (typeof enrichedCommande === 'object') {
        enrichedCommande = { ...enrichedCommande, client: clientInfo };
      }
    }

    return {
      ...res,
      client: clientInfo,
      commande: enrichedCommande,
      offre: selected.offre,
    };
  }, [selected, reservations]);

  // Handlers
  const openReservation = (res, offre) => {
    hasModifiedRef.current = false; // Réinitialise à l'ouverture
    setSelected({ id: res.id, offre });
  };
  const openDay = (offre, jour) => setSelectedDay({ offre, jour });

  // ⚡ Fonction appelée dès qu'un compte-rendu est ajouté/modifié/supprimé
  const handleDataChanged = () => {
    hasModifiedRef.current = true; // Marque qu'il y a eu un changement
  };

  // ⚡ À la fermeture de la modale de détails (sans rechargement de page complet)
  const handleCloseDetails = () => {
    setSelected(null);
    // Si une modification a eu lieu, on rafraîchit les données de façon asynchrone en arrière-plan
    if (hasModifiedRef.current && fetchReservations) {
      fetchReservations();
    } else if (fetchReservations) {
      fetchReservations(); // Simple rafraîchissement par sécurité
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#4318FF' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: '100vh' }}>
      <PlanningHeader
        currentMonth={currentMonth}
        vue={vue}
        onVueChange={setVue}
        onScroll={scrollByDays}
        onToday={scrollToToday}
      />

      {!isIntervenant && !isPraticien && (
        <IntervenantFilter
          intervenants={intervenants}
          value={selectedIntervenant}
          onChange={setSelectedIntervenant}
        />
      )}

      <TimelineGrid
        scrollContainerRef={scrollContainerRef}
        onScroll={handleScroll}
        joursTimeline={joursTimeline}
        indexToday={indexToday}
        aujourdhuiStr={aujourdhuiStr}
        largeurCellule={largeurCellule}
        offres={filteredOffres}
        reservations={filteredReservations}
        isIntervenant={isIntervenant}
        onReservationClick={openReservation}
        onOpenDay={openDay}
      />

      <DayPlanningDialog
        selectedDay={selectedDay}
        reservations={reservations}
        isIntervenant={isIntervenant}
        onClose={() => setSelectedDay(null)}
        onStatusChange={updateReservationStatus}
        onOpenDetails={openReservation}
      />

      <ReservationDetailsDialog
        reservation={selectedReservation}
        isIntervenant={isIntervenant}
        isPraticien={isPraticien}
        apiUrl={apiUrl}
        token={token}
        onClose={handleCloseDetails}
        onStatusChange={updateReservationStatus}
        onSuccess={handleDataChanged}
        // ⚡ Mettre à jour la commande en direct dans l'état global
        onOrderUpdated={(updatedCommand) => {
          hasModifiedRef.current = true;
          if (fetchReservations) {
            fetchReservations();
          }
        }}
      />
    </Box>
  );
};

export default PlanningPro;